import { Document, Page, Text, View, StyleSheet, Font, pdf, Image } from '@react-pdf/renderer';
import PropTypes from 'prop-types';
import { useState } from 'react';
import appLogo from '../../assets/logo/IconOnly_Transparent_NoBuffer.png';
import ConfirmationDialog from '../../components/dialogs/ConfirmationDialog';

// Register Hebrew font
Font.register({
  family: 'Noto',
  fonts: [
    {
      src: '/fonts/noto/static/NotoSansHebrew-Regular.ttf',
      fontWeight: 400,
    },
    {
      src: '/fonts/noto/static/NotoSansHebrew-Bold.ttf',
      fontWeight: 700,
    },
  ]
});

/**
 * Renders text with proper directional handling for mixed Hebrew and English content.
 * Based on the solution from CPRLogPDF.tsx
 *
 * Handles:
 * 1. Pure Hebrew text (RTL)
 * 2. Pure English/numbers text (LTR)
 * 3. Mixed content with both Hebrew and English
 * 4. Punctuation marks
 * 5. Proper whitespace preservation between different scripts
 */
const renderTextWithDirections = (text) => {
  if (!text) return <Text></Text>;

  // Split text into segments: whitespace, numbers with decimals, punctuation, English, Hebrew
  const segments = text.split(/(\s+)|([0-9]+.[0-9]+)|([,.\-_:;()'"]+)|([a-zA-Z0-9][/a-zA-Z0-9\s]*)|([־\u0590-\u05FF]+)/g)
    .filter(segment => segment !== '' && segment !== undefined);

  const combinedSegments = [];
  let currentEnglishSegment = '';

  segments.forEach((segment) => {
    // Handle whitespace
    if (/^\s+$/.test(segment)) {
      if (currentEnglishSegment) {
        combinedSegments.push({
          text: currentEnglishSegment.trim(),
          isEnglish: true,
          isSpace: false,
          isPunctuation: false
        });
        currentEnglishSegment = '';
      }

      combinedSegments.push({
        text: ' ',
        isEnglish: false,
        isSpace: true,
        isPunctuation: false
      });
      return;
    }

    // Handle punctuation
    if (/^[,.\-_:;()'"]+$/.test(segment)) {
      if (currentEnglishSegment) {
        combinedSegments.push({
          text: currentEnglishSegment.trim(),
          isEnglish: true,
          isSpace: false,
          isPunctuation: false
        });
        currentEnglishSegment = '';
      }

      combinedSegments.push({
        text: segment,
        isEnglish: false,
        isSpace: false,
        isPunctuation: true
      });
      return;
    }

    // Check if segment is English
    const isEnglish = /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/.test(segment);

    if (isEnglish) {
      currentEnglishSegment = currentEnglishSegment
        ? `${currentEnglishSegment}${segment}`
        : segment;
    } else {
      if (currentEnglishSegment) {
        combinedSegments.push({
          text: currentEnglishSegment.trim(),
          isEnglish: true,
          isSpace: false,
          isPunctuation: false
        });
        currentEnglishSegment = '';

        if (!combinedSegments[combinedSegments.length - 1]?.isSpace) {
          combinedSegments.push({
            text: ' ',
            isEnglish: false,
            isSpace: true,
            isPunctuation: false
          });
        }
      }

      combinedSegments.push({
        text: segment,
        isEnglish: false,
        isSpace: false,
        isPunctuation: false
      });
    }
  });

  // Handle remaining English segment
  if (currentEnglishSegment) {
    if (combinedSegments.length > 0 &&
        !combinedSegments[combinedSegments.length - 1].isEnglish &&
        !combinedSegments[combinedSegments.length - 1].isSpace) {
      combinedSegments.push({
        text: ' ',
        isEnglish: false,
        isSpace: true,
        isPunctuation: false
      });
    }

    combinedSegments.push({
      text: currentEnglishSegment.trim(),
      isEnglish: true,
      isSpace: false,
      isPunctuation: false
    });
  }

  // Remove duplicate spaces
  const cleanedSegments = combinedSegments.reduce((acc, curr, idx, arr) => {
    if (curr.isSpace && arr[idx + 1]?.isSpace) {
      return acc;
    }
    acc.push(curr);
    return acc;
  }, []);

  // Render segments in RTL container
  return (
    <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
      {cleanedSegments.map((segment, index) => (
        <Text
          key={index}
          style={{
            fontFamily: 'Noto',
            color: '#1D426E',
            direction: segment.isEnglish ? 'ltr' : 'rtl',
          }}
        >
          {segment.text}
        </Text>
      ))}
    </View>
  );
};

// Create styles
const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontFamily: 'Noto',
    backgroundColor: 'white',
    color: '#1D426E',
  },
  header: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
    minHeight: 22,
  },
  headerText: {
    textAlign: 'right',
    fontSize: 8,
    color: '#1D426E',
  },
  headerTitleContainer: {
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1D426E',
    textAlign: 'center',
  },
  headerSubtitle: {
    fontSize: 10,
    color: '#1D426E',
    textAlign: 'center',
    marginTop: 3,
  },
  headerLogos: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 22,
  },
  appLogo: {
    maxHeight: 22,
    objectFit: 'contain'
  },
  container: {
    flex: 1,
    direction: 'rtl',
  },
  group: {
    marginBottom: 8,
  },
  groupTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 4,
    textAlign: 'right',
    color: '#1D426E',
    borderBottomWidth: 1,
    borderBottomColor: '#1D426E',
    borderBottomStyle: 'solid',
    paddingBottom: 2,
  },
  itemsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  itemColumn: {
    width: '48%',
    marginBottom: 3,
  },
  item: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    marginBottom: 3,
  },
  checkbox: {
    width: 9,
    height: 9,
    borderWidth: 1,
    borderColor: '#1D426E',
    borderStyle: 'solid',
    marginLeft: 4,
    marginTop: 1,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    backgroundColor: 'white',
  },
  checkboxChecked: {
    backgroundColor: '#1D426E',
  },
  checkmark: {
    fontSize: 7,
    color: 'white',
    fontWeight: 'bold',
  },
  itemContent: {
    flex: 1,
  },
  itemText: {
    fontSize: 8,
    textAlign: 'right',
    color: '#1D426E',
    lineHeight: 1.2,
  },
  timestamp: {
    fontSize: 6,
    color: '#666',
    textAlign: 'right',
    marginTop: 1,
  },
  optionalFieldsContainer: {
    marginRight: 13,
    marginTop: 2,
    marginBottom: 2,
  },
  optionalField: {
    marginBottom: 1,
  },
  fieldLabel: {
    fontSize: 6,
    fontWeight: 'bold',
    color: '#1D426E',
    textAlign: 'right',
    marginBottom: 1,
  },
  fieldValue: {
    fontSize: 6,
    color: '#333',
    textAlign: 'right',
    paddingRight: 5,
    lineHeight: 1.1,
  },
  text: {
    fontFamily: 'Noto',
    textAlign: 'right',
    color: '#1D426E',
  },
});

// PDF Document Component
const ChecklistPDFDocument = ({ instance, patientInfo }) => {
  const formatCreationDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year}  ${hours}:${minutes}`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}  ${hours}:${minutes}`;
  };

  // Helper to get value from optionalFieldValues array
  const getOptionalFieldValue = (item, fieldLabel) => {
    if (!item.optionalFieldValues || !Array.isArray(item.optionalFieldValues)) {
      return '';
    }
    const fieldValue = item.optionalFieldValues.find(fv => fv.label === fieldLabel);
    return fieldValue?.value || '';
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text style={styles.text}>{formatCreationDate(new Date())}</Text>
          </View>
          <View style={styles.headerTitleContainer}>
            <View style={styles.headerTitle}>{renderTextWithDirections(instance.name || 'צ׳קליסט')}</View>
            {patientInfo && (
              <View style={styles.headerSubtitle}>{renderTextWithDirections(patientInfo)}</View>
            )}
          </View>
          <View style={styles.headerLogos}>
            <Image
              src={appLogo}
              style={styles.appLogo}
            />
          </View>
        </View>

        <View style={styles.container}>
          {instance.groups.map((group, groupIdx) => {
            // Split items into two columns
            const items = group.items || [];
            const midpoint = Math.ceil(items.length / 2);
            const leftColumn = items.slice(0, midpoint);
            const rightColumn = items.slice(midpoint);

            return (
              <View key={group._id || `group-${groupIdx}`} style={styles.group}>
                <View style={styles.groupTitle}>{renderTextWithDirections(group.title)}</View>

                <View style={styles.itemsContainer}>
                  {/* Right column (displays first in RTL) */}
                  <View style={styles.itemColumn}>
                    {rightColumn.map((item, itemIdx) => {
                      const hasInputFields = item.optionalFields &&
                        item.optionalFields.some(field => field.type === 'input' && getOptionalFieldValue(item, field.label));

                      return (
                        <View key={item._id || `item-right-${itemIdx}`}>
                          <View style={styles.item}>
                            <View style={[styles.checkbox, item.isChecked && styles.checkboxChecked]}>
                              {item.isChecked && <Text style={styles.checkmark}>✓</Text>}
                            </View>
                            <View style={styles.itemContent}>
                              <View style={styles.itemText}>{renderTextWithDirections(item.text)}</View>
                              {item.isChecked && item.lastChanged && (
                                <View style={styles.timestamp}>
                                  <Text style={styles.text}>{formatTimestamp(item.lastChanged)}</Text>
                                </View>
                              )}
                            </View>
                          </View>

                          {hasInputFields && (
                            <View style={styles.optionalFieldsContainer}>
                              {item.optionalFields
                                .filter(field => field.type === 'input')
                                .map((field, fieldIdx) => {
                                  const value = getOptionalFieldValue(item, field.label);
                                  if (!value) return null;

                                  return (
                                    <View key={fieldIdx} style={styles.optionalField}>
                                      <View style={styles.fieldLabel}>{renderTextWithDirections(field.label + ':')}</View>
                                      <View style={styles.fieldValue}>{renderTextWithDirections(value)}</View>
                                    </View>
                                  );
                                })}
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </View>

                  {/* Left column */}
                  <View style={styles.itemColumn}>
                    {leftColumn.map((item, itemIdx) => {
                      const hasInputFields = item.optionalFields &&
                        item.optionalFields.some(field => field.type === 'input' && getOptionalFieldValue(item, field.label));

                      return (
                        <View key={item._id || `item-left-${itemIdx}`}>
                          <View style={styles.item}>
                            <View style={[styles.checkbox, item.isChecked && styles.checkboxChecked]}>
                              {item.isChecked && <Text style={styles.checkmark}>✓</Text>}
                            </View>
                            <View style={styles.itemContent}>
                              <View style={styles.itemText}>{renderTextWithDirections(item.text)}</View>
                              {item.isChecked && item.lastChanged && (
                                <View style={styles.timestamp}>
                                  <Text style={styles.text}>{formatTimestamp(item.lastChanged)}</Text>
                                </View>
                              )}
                            </View>
                          </View>

                          {hasInputFields && (
                            <View style={styles.optionalFieldsContainer}>
                              {item.optionalFields
                                .filter(field => field.type === 'input')
                                .map((field, fieldIdx) => {
                                  const value = getOptionalFieldValue(item, field.label);
                                  if (!value) return null;

                                  return (
                                    <View key={fieldIdx} style={styles.optionalField}>
                                      <View style={styles.fieldLabel}>{renderTextWithDirections(field.label + ':')}</View>
                                      <View style={styles.fieldValue}>{renderTextWithDirections(value)}</View>
                                    </View>
                                  );
                                })}
                            </View>
                          )}
                        </View>
                      );
                    })}
                  </View>
                </View>
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
};

ChecklistPDFDocument.propTypes = {
  instance: PropTypes.object.isRequired,
  patientInfo: PropTypes.string,
};

// Export Button Component
export const ExportChecklistButton = ({ instance, onExportComplete }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [patientInfo, setPatientInfo] = useState('');

  const handleExport = async () => {
    if (!instance) return;

    try {
      const blob = await pdf(<ChecklistPDFDocument instance={instance} patientInfo={patientInfo} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Use patient info in filename if provided
      const baseFileName = patientInfo
        ? `${patientInfo.replace(/\s+/g, '_')}-${instance.name || 'checklist'}`
        : instance.name
          ? `${instance.name.replace(/\s+/g, '_')}`
          : 'checklist';

      const fileName = `${baseFileName}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Reset and close dialog
      setIsDialogOpen(false);
      setPatientInfo('');

      // Call the onExportComplete callback if provided
      if (onExportComplete) {
        onExportComplete();
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('שגיאה ביצירת קובץ PDF');
    }
  };

  return (
    <>
      <button
        onClick={() => setIsDialogOpen(true)}
        disabled={!instance}
        style={{
          padding: '12px 24px',
          border: '1px solid #103c6e',
          borderRadius: '8px',
          fontSize: '1rem',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
      >
        ייצוא ל-PDF
      </button>

      <ConfirmationDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={handleExport}        
        confirmText="ייצא ל-PDF"
        cancelText="ביטול"
      >
        <input
          type="text"
          data-clarity-mask="true"
          value={patientInfo}
          onChange={(e) => setPatientInfo(e.target.value)}
          placeholder="שם המטופל \ מיטה \ חדר"
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '1rem',
            border: '1px solid #ccc',
            borderRadius: '8px',
            textAlign: 'right',
            direction: 'rtl',
          }}
        />
      </ConfirmationDialog>
    </>
  );
};

ExportChecklistButton.propTypes = {
  instance: PropTypes.object,
  onExportComplete: PropTypes.func,
};

export default ChecklistPDFDocument;
